// Phase 38 · 23b — the rules behind the dashboard widgets.
//
// 23b makes four arguments about the widget grid, and three of them are rules
// rather than styling, so they live here where they can be tested:
//
//  1. A percentage on its own is not a finding. 9.0% SLA compliance means
//     "193 of 212 breached", and the count is what makes it act-on-able — but
//     only when the count and the percentage describe the SAME population.
//     `slaBreachCount` refuses to pair them when they disagree.
//  2. A real zero is a statement, not a missing value. 0.0% AI resolution means
//     nothing was resolved without a human. Distinct from "no data", which the
//     KPI cards already render as an em-dash.
//  3. A ranked list beats a donut: five slices need five colours and still make
//     12% and 9% hard to compare. `ranked` gives count, share and ordering at
//     once, and keeps zero rows because an absent category is a fact.
//
// The fourth (every card states its finding in a foot band) is phrasing, and
// lives with each widget.

import type { SlaMetrics } from '@services/analytics.service';

// --------------------------------------------------------------- ranked list

export interface RankedRow {
  key: string;
  label: string;
  value: number;
  /** Percent of the total, 0-100. Null when the total is 0 — never 0%. */
  share: number | null;
  /** Width of the bar relative to the largest row, 0-100. */
  weight: number;
  color?: string;
}

export interface RankInput {
  key?: string;
  label: string;
  value: number;
  color?: string;
}

/**
 * Rank by value, largest first, ties broken alphabetically so the order is
 * stable across refetches.
 *
 * Bars are sized against the LARGEST row, not the total: the point of a ranked
 * list is comparing rows to each other, and scaling by the total leaves every
 * bar short and the comparison harder than the donut it replaced. The share
 * column still carries the proportion.
 */
export function ranked(items: RankInput[] | undefined | null): RankedRow[] {
  if (!items || items.length === 0) return [];
  const total = items.reduce((sum, i) => sum + (i.value || 0), 0);
  const max = items.reduce((m, i) => Math.max(m, i.value || 0), 0);
  return [...items]
    .sort((a, b) => (b.value || 0) - (a.value || 0) || a.label.localeCompare(b.label))
    .map((i) => ({
      key: i.key || i.label,
      label: i.label,
      value: i.value || 0,
      share: total > 0 ? ((i.value || 0) / total) * 100 : null,
      weight: max > 0 ? ((i.value || 0) / max) * 100 : 0,
      color: i.color,
    }));
}

/** The largest row, for the foot band's finding. Null on an empty list. */
export function leader(rows: RankedRow[]): RankedRow | null {
  return rows.length > 0 && rows[0].value > 0 ? rows[0] : null;
}

// ------------------------------------------------------------ the SLA figure

export interface BreachCount {
  breached: number;
  total: number;
}

/**
 * The count behind the SLA percentage.
 *
 * The KPI card's headline is `performance.firstResponseSlaCompliance`; the
 * counts live on `slaData.firstResponse`. They are computed by different calls,
 * so this pairs them ONLY when the dimension's own percentage renders to the
 * same figure the card is showing. When they disagree the honest move is to
 * print no count rather than a count that belongs to a different number.
 */
export function slaBreachCount(
  headlinePct: number | null | undefined,
  sla: SlaMetrics | undefined | null
): BreachCount | null {
  if (headlinePct === null || headlinePct === undefined) return null;
  const fr = sla?.firstResponse;
  if (!fr) return null;
  const total = (fr.compliant || 0) + (fr.breached || 0);
  if (total <= 0) return null;
  // Same population only if the two sources round to the same displayed figure.
  if ((fr.percentage ?? NaN).toFixed(1) !== headlinePct.toFixed(1)) return null;
  return { breached: fr.breached || 0, total };
}

// --------------------------------------------------------- what a zero means

/**
 * A metric that is genuinely 0 says something; the card should say it. Returns
 * null for metrics where zero has no reading beyond the number itself.
 *
 * These are only ever reached for a REAL zero — a missing or null metric is
 * rendered as an em-dash by the card and never gets here.
 */
export function zeroMeaning(kpiType: string): string | null {
  switch (kpiType) {
    case 'kpi_ai_resolution_rate':
      return 'Nothing resolved without a human';
    case 'kpi_sla_compliance':
      return 'Every ticket with a target missed it';
    default:
      return null;
  }
}

// ------------------------------------------------------------ volume vs work

export interface VolumePoint {
  name: string;
  created: number;
  resolved: number;
}

export interface VolumeFinding {
  totalCreated: number;
  totalResolved: number;
  /** The busiest intake day, for naming the spike. */
  peak: VolumePoint;
  /** Days where resolved fell short of created. */
  daysBehind: number;
  days: number;
  /**
   * What one point of the series is. The chart is bucketed by day, week or
   * month depending on the range, and the caption has to count in the same
   * unit: ten monthly points are not "10 days".
   */
  unit: VolumeUnit;
  /** True when resolution matched or beat intake on at least one day. */
  everCaughtUp: boolean;
}

export type VolumeUnit = 'day' | 'week' | 'month';

const MONTH_BUCKET = /^\d{4}-\d{2}$/;
const WEEK_BUCKET = /^\d{4}-W\d{1,2}$/i;

/** The bucket size of a series, read from its labels ("2026-10", "2026-W41"). */
export function volumeUnit(points: VolumePoint[]): VolumeUnit {
  if (points.length > 0 && points.every((p) => MONTH_BUCKET.test(p.name))) return 'month';
  if (points.length > 0 && points.every((p) => WEEK_BUCKET.test(p.name))) return 'week';
  return 'day';
}

/** "2026-10" → "October 2026"; any other label is returned as it is. */
export function volumeBucketLabel(name: string): string {
  if (!MONTH_BUCKET.test(name)) return name;
  const [year, month] = name.split('-').map(Number);
  if (month < 1 || month > 12) return name;
  const MONTHS = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ];
  return `${MONTHS[month - 1]} ${year}`;
}

/**
 * Whether the team is keeping up, stated in the terms the chart is drawn in.
 * Null for an empty series — a finding about no data is not a finding.
 */
export function volumeFinding(points: VolumePoint[] | undefined | null): VolumeFinding | null {
  if (!points || points.length === 0) return null;
  let totalCreated = 0;
  let totalResolved = 0;
  let daysBehind = 0;
  let peak = points[0];
  for (const p of points) {
    const created = p.created || 0;
    const resolved = p.resolved || 0;
    totalCreated += created;
    totalResolved += resolved;
    if (resolved < created) daysBehind += 1;
    if (created > (peak.created || 0)) peak = p;
  }
  return {
    totalCreated,
    totalResolved,
    peak,
    daysBehind,
    days: points.length,
    unit: volumeUnit(points),
    everCaughtUp: daysBehind < points.length,
  };
}

// --------------------------------------------------------------- agent load

export interface LoadRow {
  name: string;
  open: number;
}

export interface LoadFinding {
  /** Whoever holds the most open work. */
  holder: string;
  held: number;
  total: number;
  /** Roster members holding nothing — an empty row is a staffing fact. */
  idle: number;
}

/**
 * Who is carrying the queue. Reported only when one person holds more than
 * their even share by a clear margin, so the foot band does not manufacture a
 * concentration story out of an evenly spread team.
 */
export function loadFinding(rows: LoadRow[] | undefined | null): LoadFinding | null {
  if (!rows || rows.length === 0) return null;
  const total = rows.reduce((sum, r) => sum + (r.open || 0), 0);
  if (total <= 0) return null;
  const top = [...rows].sort((a, b) => (b.open || 0) - (a.open || 0))[0];
  const evenShare = total / rows.length;
  if ((top.open || 0) <= evenShare * 1.5) return null;
  return {
    holder: top.name,
    held: top.open || 0,
    total,
    idle: rows.filter((r) => (r.open || 0) === 0).length,
  };
}
